import api, { handleResponse, handleError } from './api';

const clubService = {
  // Core CRUD
  async getPublicClubs(search, page = 1, pageSize = 20) {
    try {
      const params = { page, pageSize };
      if (search) params.search = search;
      const response = await api.get('/clubs', { params });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async createClub(data) {
    try {
      const response = await api.post('/clubs', data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async getClub(id) {
    try {
      const response = await api.get(`/clubs/${id}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // ---- Forum (Phase 4) ----
  /** Categories + threads + permissions in one trip (the forum page). */
  async getForumOverview(clubId, categoryId = null) {
    try {
      const qs = categoryId ? `?categoryId=${categoryId}` : '';
      return handleResponse(await api.get(`/clubs/${clubId}/forum/overview${qs}`));
    } catch (e) { return handleError(e); }
  },
  async listForumCategories(clubId) {
    try { return handleResponse(await api.get(`/clubs/${clubId}/forum/categories`)); } catch (e) { return handleError(e); }
  },
  async createForumCategory(clubId, data) {
    try { return handleResponse(await api.post(`/clubs/${clubId}/forum/categories`, data)); } catch (e) { return handleError(e); }
  },
  async updateForumCategory(clubId, categoryId, data) {
    try { return handleResponse(await api.put(`/clubs/${clubId}/forum/categories/${categoryId}`, data)); } catch (e) { return handleError(e); }
  },
  async deleteForumCategory(categoryId) {
    try { return handleResponse(await api.delete(`/clubs/forum/categories/${categoryId}`)); } catch (e) { return handleError(e); }
  },
  /** Club members the @-picker can offer. */
  async listForumMembers(clubId) {
    try { return handleResponse(await api.get(`/clubs/${clubId}/forum/members`)); } catch (e) { return handleError(e); }
  },

  async listForumThreads(clubId) {
    try { return handleResponse(await api.get(`/clubs/${clubId}/forum`)); } catch (e) { return handleError(e); }
  },
  async createForumThread(clubId, data) {
    try { return handleResponse(await api.post(`/clubs/${clubId}/forum`, data)); } catch (e) { return handleError(e); }
  },
  async getForumThread(threadId) {
    try { return handleResponse(await api.get(`/clubs/forum/threads/${threadId}`)); } catch (e) { return handleError(e); }
  },
  async addForumReply(threadId, data) {
    try { return handleResponse(await api.post(`/clubs/forum/threads/${threadId}/replies`, data)); } catch (e) { return handleError(e); }
  },
  /** Owner/admin only — promotes a thread to the top of the forum. */
  async setForumThreadPinned(threadId, pinned) {
    try { return handleResponse(await api.post(`/clubs/forum/threads/${threadId}/pin?pinned=${pinned}`)); } catch (e) { return handleError(e); }
  },
  async deleteForumThread(threadId) {
    try { return handleResponse(await api.delete(`/clubs/forum/threads/${threadId}`)); } catch (e) { return handleError(e); }
  },
  async deleteForumReply(replyId) {
    try { return handleResponse(await api.delete(`/clubs/forum/replies/${replyId}`)); } catch (e) { return handleError(e); }
  },

  async updateClub(id, data) {
    try {
      const response = await api.put(`/clubs/${id}`, data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async deleteClub(id) {
    try {
      const response = await api.delete(`/clubs/${id}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async getClubByInviteCode(code) {
    try {
      const response = await api.get(`/clubs/invite/${code}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async joinByInviteCode(code) {
    try {
      const response = await api.post(`/clubs/invite/${code}/join`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /** A deleted club's old URL: { id, name, avatarUrl, deletedAt, restoreUntil, canRestore, memberCount }. */
  async getClosed(id) {
    try { return handleResponse(await api.get(`/clubs/${id}/closed`)); } catch (error) { return handleError(error); }
  },
  /** Owner-only, inside the 30-day window. Answers the live ClubDto. */
  async restoreClub(id) {
    try { return handleResponse(await api.post(`/clubs/${id}/restore`)); } catch (error) { return handleError(error); }
  },
  /** The caller's deleted clubs still inside the window. */
  async getMyDeletedClubs() {
    try { return handleResponse(await api.get('/clubs/deleted/mine')); } catch (error) { return handleError(error); }
  },

  async getMyClubs() {
    try {
      const response = await api.get('/clubs/my');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Membership
  async joinClub(id) {
    try {
      const response = await api.post(`/clubs/${id}/join`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async leaveClub(id) {
    try {
      const response = await api.post(`/clubs/${id}/leave`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async getMembers(id, page = 1, pageSize = 50) {
    try {
      const response = await api.get(`/clubs/${id}/members`, { params: { page, pageSize } });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async removeMember(clubId, userId, block = false) {
    try {
      const response = await api.delete(`/clubs/${clubId}/members/${userId}`, { params: { block } });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async updateMemberRole(clubId, userId, role) {
    try {
      const response = await api.put(`/clubs/${clubId}/members/${userId}/role`, { role });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async transferOwnership(clubId, newOwnerId) {
    try {
      const response = await api.post(`/clubs/${clubId}/transfer-ownership`, { newOwnerId });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Join requests / approval flow
  async getPendingJoinRequests(clubId) {
    try {
      const response = await api.get(`/clubs/${clubId}/pending-requests`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async acceptJoinRequest(clubId, requestId) {
    try {
      const response = await api.post(`/clubs/${clubId}/pending-requests/${requestId}/accept`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async declineJoinRequest(clubId, requestId) {
    try {
      const response = await api.post(`/clubs/${clubId}/pending-requests/${requestId}/decline`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async blockJoinRequestApplicant(clubId, requestId) {
    try {
      const response = await api.post(`/clubs/${clubId}/pending-requests/${requestId}/block`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async getBlockedUsers(clubId) {
    try {
      const response = await api.get(`/clubs/${clubId}/blocked-users`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async unblockUser(clubId, userId) {
    try {
      const response = await api.delete(`/clubs/${clubId}/blocked-users/${userId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Announcements
  async getAnnouncements(clubId, page = 1, pageSize = 20) {
    try {
      const response = await api.get(`/clubs/${clubId}/announcements`, { params: { page, pageSize } });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async createAnnouncement(clubId, data) {
    try {
      const response = await api.post(`/clubs/${clubId}/announcements`, data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async updateAnnouncement(announcementId, data) {
    try {
      const response = await api.put(`/clubs/announcements/${announcementId}`, data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async deleteAnnouncement(announcementId) {
    try {
      const response = await api.delete(`/clubs/announcements/${announcementId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async reactToAnnouncement(announcementId, emoji) {
    try {
      const response = await api.post(`/clubs/announcements/${announcementId}/react`, { emoji });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Polls
  async getPolls(clubId, activeOnly = false) {
    try {
      const response = await api.get(`/clubs/${clubId}/polls`, { params: { activeOnly } });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async createPoll(clubId, data) {
    try {
      const response = await api.post(`/clubs/${clubId}/polls`, data);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async voteOnPoll(pollId, optionIndex) {
    try {
      const response = await api.post(`/clubs/polls/${pollId}/vote`, { optionIndex });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async closePoll(pollId) {
    try {
      const response = await api.post(`/clubs/polls/${pollId}/close`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async deletePoll(pollId) {
    try {
      const response = await api.delete(`/clubs/polls/${pollId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Events
  async getClubEvents(clubId, page = 1, pageSize = 20, includeHidden = false) {
    try {
      const response = await api.get(`/clubs/${clubId}/events`, { params: { page, pageSize, includeHidden } });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async linkEventToClub(clubId, eventId) {
    try {
      const response = await api.post(`/clubs/${clubId}/events/${eventId}/link`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async setClubEventHidden(clubId, eventId, hidden) {
    try {
      const response = await api.post(`/clubs/${clubId}/events/${eventId}/hide`, { hidden });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async unlinkEventFromClub(clubId, eventId) {
    try {
      const response = await api.delete(`/clubs/${clubId}/events/${eventId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Invitations
  async getInvitableFriends(clubId) {
    try {
      const response = await api.get(`/clubs/${clubId}/invitable-friends`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async inviteFriend(clubId, userId) {
    try {
      const response = await api.post(`/clubs/${clubId}/invite`, { userId });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // Avatar upload
  // ── Cover (Sept 2026 club pages redesign): photo pool, layout, framing, card photo, video ──

  /** Upload one cover photo → { path, url }. The editor then PUTs `cover` with `addPaths`. */
  async uploadCoverPhoto(clubId, file) {
    try {
      const form = new FormData();
      form.append('file', file);
      return handleResponse(await api.post(`/clubs/${clubId}/photos/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } }));
    } catch (error) { return handleError(error); }
  },

  /** EDIT COVER: layout · split · photoCount · order · focal · cardId · addPaths · remove. */
  async updateCover(clubId, data) {
    try { return handleResponse(await api.put(`/clubs/${clubId}/cover`, data)); }
    catch (error) { return handleError(error); }
  },

  async startCoverVideoUpload(clubId) {
    try { return handleResponse(await api.post(`/clubs/${clubId}/cover-video/upload-url`)); }
    catch (error) { return handleError(error); }
  },
  async setCoverVideoEmbed(clubId, url) {
    try { return handleResponse(await api.put(`/clubs/${clubId}/cover-video/embed`, { url })); }
    catch (error) { return handleError(error); }
  },
  async getCoverVideo(clubId) {
    try { return handleResponse(await api.get(`/clubs/${clubId}/cover-video`)); }
    catch (error) { return handleError(error); }
  },
  async clearCoverVideo(clubId) {
    try { return handleResponse(await api.delete(`/clubs/${clubId}/cover-video`)); }
    catch (error) { return handleError(error); }
  },

  async uploadAvatar(clubId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/clubs/${clubId}/avatar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async uploadBanner(clubId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/clubs/${clubId}/banner`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async uploadBackground(clubId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/clubs/${clubId}/background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  async removeClubImage(clubId, imageType) {
    try {
      const response = await api.delete(`/clubs/${clubId}/image/${imageType}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * The read-only public face of a club — name, blurb, imagery, and counts.
   * Anonymous: this is what a logged-out visitor and a crawler get. Never returns
   * the member list, forum, announcements, polls, or invite code. Accepts the
   * club's slug or its id. Private clubs 404.
   */
  async getPublicClub(slugOrId) {
    try {
      const response = await api.get(`/clubs/public/${encodeURIComponent(slugOrId)}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  // ---- Events + clubs redesign (Sept 2026) — additive; every endpoint here may 404 while the
  //      backend lands, and the club page hides the block rather than crashing. ----

  /** The caller's own membership row (ClubMembershipDto) or null. GET /clubs/{id}/membership/me */
  async getMyMembership(clubId) {
    try { return handleResponse(await api.get(`/clubs/${clubId}/membership/me`)); } catch (e) { return handleError(e); }
  },

  /** Deletes the caller's pending join request. POST /clubs/{id}/withdraw-request */
  async withdrawJoinRequest(clubId) {
    try { return handleResponse(await api.post(`/clubs/${clubId}/withdraw-request`)); } catch (e) { return handleError(e); }
  },

  // Announcement replies — the expandable thread under a POST / PINNED card.
  async listAnnouncementReplies(announcementId) {
    try { return handleResponse(await api.get(`/clubs/announcements/${announcementId}/replies`)); } catch (e) { return handleError(e); }
  },
  async addAnnouncementReply(announcementId, body) {
    try { return handleResponse(await api.post(`/clubs/announcements/${announcementId}/replies`, { body })); } catch (e) { return handleError(e); }
  },
  async deleteAnnouncementReply(replyId) {
    try { return handleResponse(await api.delete(`/clubs/announcements/replies/${replyId}`)); } catch (e) { return handleError(e); }
  },
};

export default clubService;
